import { useEffect, useState } from 'react';

import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
} from '@mui/material';

import type {
  ProviderMode,
  ProviderSummary,
  ProviderUpsertRequest,
} from '../../model/settingsTypes';

export interface ProviderFormDialogProps {
  open: boolean;
  mode: 'create' | 'edit';
  initial: ProviderSummary | null;
  canManageCredentials: boolean;
  credentialHint?: string;
  saving: boolean;
  onClose: () => void;
  onSubmit: (body: ProviderUpsertRequest) => void;
}

const MODE_OPTIONS: { value: ProviderMode; label: string }[] = [
  { value: 'SANDBOX', label: 'Sandbox' },
  { value: 'PRODUCTION', label: 'Production' },
];

/** Create/edit form for a delivery, payment, or notification provider. */
export function ProviderFormDialog({
  open,
  mode,
  initial,
  canManageCredentials,
  credentialHint = 'API key / token',
  saving,
  onClose,
  onSubmit,
}: ProviderFormDialogProps) {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [priority, setPriority] = useState<string>('');
  const [providerMode, setProviderMode] = useState<ProviderMode>('SANDBOX');
  const [secret, setSecret] = useState('');
  const [settingsText, setSettingsText] = useState('');
  const [settingsError, setSettingsError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (open) {
      setCode(initial?.code ?? '');
      setName(initial?.name ?? '');
      setEnabled(initial?.enabled ?? true);
      setPriority(initial?.priority != null ? String(initial.priority) : '');
      setProviderMode(initial?.mode ?? 'SANDBOX');
      setSecret('');
      setSettingsText('');
      setSettingsError(null);
      setSubmitted(false);
    }
  }, [open, initial]);

  const codeError = submitted && code.trim() === '';
  const nameError = submitted && name.trim() === '';

  const handleSubmit = () => {
    setSubmitted(true);
    if (code.trim() === '' || name.trim() === '') {
      return;
    }

    let parsedSettings: Record<string, unknown> | undefined;
    if (settingsText.trim() !== '') {
      try {
        parsedSettings = JSON.parse(settingsText) as Record<string, unknown>;
      } catch {
        setSettingsError('Settings must be valid JSON.');
        return;
      }
    }

    const priorityValue = priority.trim() === '' ? null : Number(priority);

    const body: ProviderUpsertRequest = {
      code: code.trim(),
      name: name.trim(),
      enabled,
      priority: priorityValue,
      mode: providerMode,
      ...(canManageCredentials && secret.trim() !== '' ? { secret: secret } : {}),
      ...(parsedSettings ? { settings: parsedSettings } : {}),
    };

    onSubmit(body);
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{mode === 'create' ? 'Add provider' : 'Edit provider'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <TextField
            label="Code"
            value={code}
            onChange={(event) => setCode(event.target.value)}
            error={codeError}
            helperText={codeError ? 'Code is required.' : 'Stable provider code, e.g. cdek.'}
            size="small"
            fullWidth
            required
          />
          <TextField
            label="Display name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            error={nameError}
            helperText={nameError ? 'Name is required.' : undefined}
            size="small"
            fullWidth
            required
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Priority"
              value={priority}
              onChange={(event) => setPriority(event.target.value)}
              type="number"
              size="small"
              fullWidth
              helperText="Lower runs first."
            />
            <TextField
              label="Mode"
              value={providerMode}
              onChange={(event) => setProviderMode(event.target.value as ProviderMode)}
              select
              size="small"
              fullWidth
            >
              {MODE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          {canManageCredentials ? (
            <TextField
              label="Secret"
              value={secret}
              onChange={(event) => setSecret(event.target.value)}
              type="password"
              size="small"
              fullWidth
              autoComplete="new-password"
              placeholder={initial?.credential?.present ? '•••• (unchanged)' : ''}
              helperText={`${credentialHint}. Leave blank to keep the current secret.`}
            />
          ) : null}

          <TextField
            label="Advanced settings (JSON)"
            value={settingsText}
            onChange={(event) => {
              setSettingsText(event.target.value);
              setSettingsError(null);
            }}
            error={Boolean(settingsError)}
            helperText={settingsError ?? 'Optional provider-specific configuration as JSON.'}
            size="small"
            fullWidth
            multiline
            minRows={2}
          />

          <FormControlLabel
            control={
              <Switch checked={enabled} onChange={(event) => setEnabled(event.target.checked)} />
            }
            label="Enabled"
          />

          {!canManageCredentials ? (
            <Alert severity="info">
              You can manage this provider but not its credentials. The secret is unchanged.
            </Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {mode === 'create' ? 'Add provider' : 'Save changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
